import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Box,
  VStack,
  Textarea,
  FormControl,
  FormLabel,
  HStack,
  Avatar,
  Circle,
  useRadio,
  useRadioGroup,
} from '@chakra-ui/react'
import type { UseRadioProps } from '@chakra-ui/react'
import { ChakraPrimaryButton } from 'components/Button'
import { PrescriptionCheckin } from 'hooks/usePrescriptionCheckins'

export type MedicationFeeling = 'worse' | 'same' | 'better' | 'great' | ''

export type CheckinAnswers = {
  comment?: string
  medicationFeeling?: MedicationFeeling
}

interface PrescriptionCheckinModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: ({
    id,
    comment,
    medicationFeeling,
  }: {
    id: string
    comment?: string
    medicationFeeling: MedicationFeeling
  }) => void
  isLoading?: boolean
  incompleteCheckin: PrescriptionCheckin
  setCheckinAnswers: (data: CheckinAnswers) => void
  checkinAnswers: CheckinAnswers
  providerName: string
}

const FEELING_OPTIONS = [
  { value: 'worse', label: 'Worse' },
  { value: 'same', label: 'Same' },
  { value: 'better', label: 'Better' },
  { value: 'great', label: 'Great' },
]

function RadioCircle(props: UseRadioProps & { children?: React.ReactNode }) {
  const { getInputProps, getCheckboxProps, state } = useRadio(props)
  const input = getInputProps()
  const checkbox = getCheckboxProps()

  return (
    <Box as="label" mb="0px">
      <input {...input} />
      <Circle
        {...checkbox}
        size="25px"
        border="1px solid"
        borderColor="#4678E6"
        bg={state.isChecked ? '#4678E6' : 'white'}
      />
      {props.children}
    </Box>
  )
}

export const FeelingStepper: React.FC<{
  value: MedicationFeeling
  onChange: (value: MedicationFeeling) => void
}> = ({ value, onChange }) => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'feeling',
    value: value || '',
    onChange: (val) => onChange(val as MedicationFeeling),
  })

  const group = getRootProps()

  return (
    <HStack spacing={8} align="center" {...group} position="relative">
      {FEELING_OPTIONS.map((option) => (
        <Box key={option.value} textAlign="center" position="relative" flex="1">
          <RadioCircle {...getRadioProps({ value: option.value })} />
          <Text fontSize="12px" fontWeight="500" color="#4678E6">
            {option.label}
          </Text>
        </Box>
      ))}
      <Box
        position="absolute"
        width="80%"
        height="1px"
        bg="blue.400"
        left="10%"
        bottom="80%"
        zIndex={-1}
      />
    </HStack>
  )
}

const PrescriptionCheckinModal: React.FC<PrescriptionCheckinModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  incompleteCheckin,
  setCheckinAnswers,
  checkinAnswers,
  providerName,
}) => {
  const { comment = '', medicationFeeling = '' } = checkinAnswers
  const { id, provider } = incompleteCheckin
  const { headshot = '' } = provider ?? {}

  const handleSubmit = () => {
    onSubmit({ id, comment, medicationFeeling })
  }

  const handleClose = () => {
    setCheckinAnswers({ comment: '', medicationFeeling: '' })
    onClose()
  }

  const handleFeelingChange = (value: MedicationFeeling) => {
    setCheckinAnswers({ ...checkinAnswers, medicationFeeling: value })
  }

  const handleCommentChange = (value: string) => {
    setCheckinAnswers({ ...checkinAnswers, comment: value })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size="md"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxW="432px">
        <ModalHeader
          borderBottom="none"
          fontSize="24px"
          fontWeight="500"
          lineHeight="32px"
          color="var(--blue-dark)"
          p="24px 24px 8px 24px"
        >
          Medication check-in
          <ModalCloseButton
            fontWeight="400"
            color="#9091A4"
            pt="28px"
            pr="24px"
          />
        </ModalHeader>

        <ModalBody pb="24px" pt="0px">
          <VStack align="stretch" spacing={6}>
            <HStack spacing={3} alignItems="center">
              <Avatar
                size="xs"
                name={`${providerName}`}
                src={headshot}
                bg="var(--gray-light)"
                color="var(--blue-dark)"
              />
              <Text
                fontSize="16px"
                fontWeight="400"
                lineHeight="24px"
                color="var(--blue-dark)"
                mb="0px"
              >
                Requested by {providerName}
              </Text>
            </HStack>

            <Text
              fontSize="16px"
              lineHeight="24px"
              color="var(--blue-dark)"
              fontWeight="normal"
            >
              Use the scale below to let us know how you&apos;re feeling on your
              medication.
            </Text>

            <FeelingStepper
              value={medicationFeeling}
              onChange={handleFeelingChange}
            />

            <FormControl>
              <FormLabel
                fontSize="16px"
                lineHeight="20px"
                fontWeight="normal"
                color="var(--blue-dark)"
                mb="12px"
              >
                Please add any additional comments for your provider.
              </FormLabel>
              <Textarea
                value={comment}
                onChange={(e) => handleCommentChange(e.target.value)}
                placeholder="Enter your comments here"
                size="md"
                resize="vertical"
                minH="190px"
                borderColor="var(--gray-light)"
                maxLength={500}
                _placeholder={{
                  color: 'var(--gray-medium)',
                }}
              />
            </FormControl>

            <ChakraPrimaryButton
              onClick={handleSubmit}
              disabled={!medicationFeeling}
            >
              Submit
            </ChakraPrimaryButton>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default PrescriptionCheckinModal
